import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Paper, TextField, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserContext } from "../../context/UserContext";
import { mfaSetupConfirm, mfaSetupStart, mfaVerify, MfaLoginResult } from "../../client/api";
import { completeLogin } from "../../utils/completeLogin";
import LogoContainer from "../../components/LogoContainer";
import Logo from "../../components/Logo";

// SpikeUp Solutions Ops Command brand system - see spikeup-solutions-brand-book.html
const THEME_COLORS = {
  bg: "#04070a",
  panel: "#0b1318",
  panel2: "#0e1a20",
  bezel: "#1b262d",
  bezelHi: "#2c3b44",
  line: "#16242b",
  grnGlow: "rgba(55,255,163,.55)",
  cyan: "#39e0ff",
  cyanGlow: "rgba(57,224,255,.5)",
  red: "#ff4b3e",
  txt: "#9fc6c0",
  txtDim: "#5b7a78",
  ink: "#cdeee6",
} as const;

const FONT_DISPLAY = "'Orbitron', sans-serif";
const FONT_UI = "'Rajdhani', sans-serif";
const FONT_MONO = "'Share Tech Mono', monospace";

const PAGE_SX = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: `radial-gradient(120% 80% at 50% -8%, #0a1820 0%, ${THEME_COLORS.bg} 55%), ${THEME_COLORS.bg}`,
  fontFamily: FONT_UI,
  padding: 2,
};

const PANEL_SX = {
  maxWidth: 400,
  width: "100%",
  padding: 4,
  borderRadius: "11px",
  background: `linear-gradient(180deg, ${THEME_COLORS.panel2}, ${THEME_COLORS.panel})`,
  border: `1px solid ${THEME_COLORS.bezel}`,
  boxShadow: `inset 0 1px 0 ${THEME_COLORS.bezelHi}, inset 0 0 22px rgba(0,0,0,.6), 0 6px 18px rgba(0,0,0,.5)`,
};

const TEXT_SX = { color: THEME_COLORS.txt, fontFamily: FONT_UI, mb: 2 };

const INPUT_PROPS = {
  sx: {
    borderRadius: "6px",
    backgroundColor: "#040b09",
    color: THEME_COLORS.ink,
    fontFamily: FONT_MONO,
    letterSpacing: "4px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: THEME_COLORS.line },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: THEME_COLORS.bezelHi },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: THEME_COLORS.cyan,
      boxShadow: "0 0 0 2px rgba(57,224,255,0.15)",
    },
  },
};

const LABEL_PROPS = {
  sx: {
    color: THEME_COLORS.txtDim,
    fontFamily: FONT_UI,
    fontWeight: 500,
    "&.Mui-focused": { color: THEME_COLORS.cyan },
  },
};

const PRIMARY_BUTTON_SX = {
  py: 1.5,
  borderRadius: "7px",
  background: "linear-gradient(180deg, #13343f, #0a1c22)",
  border: `1px solid ${THEME_COLORS.bezelHi}`,
  color: THEME_COLORS.cyan,
  fontFamily: FONT_DISPLAY,
  fontSize: "0.85rem",
  fontWeight: 700,
  letterSpacing: "2px",
  textShadow: `0 0 10px ${THEME_COLORS.cyanGlow}`,
  boxShadow: "none",
  "&:hover": {
    background: "linear-gradient(180deg, #17404c, #0d232a)",
    borderColor: THEME_COLORS.cyan,
    boxShadow: "0 0 14px rgba(57,224,255,0.25)",
  },
  "&:disabled": {
    background: THEME_COLORS.panel,
    borderColor: THEME_COLORS.bezel,
    color: THEME_COLORS.txtDim,
    textShadow: "none",
  },
};

const LINK_BUTTON_SX = {
  textTransform: "none",
  fontFamily: FONT_UI,
  fontWeight: 500,
  color: THEME_COLORS.txtDim,
  "&:hover": { color: THEME_COLORS.cyan, backgroundColor: "transparent", textDecoration: "underline" },
};

const ALERT_SX = {
  mb: 3,
  backgroundColor: "rgba(255,75,62,0.08)",
  border: `1px solid ${THEME_COLORS.red}`,
  borderLeft: `4px solid ${THEME_COLORS.red}`,
  color: THEME_COLORS.ink,
  borderRadius: "8px",
  fontFamily: FONT_UI,
  "& .MuiAlert-icon": { color: THEME_COLORS.red },
};

type Step = "verify" | "setup";
type MfaState = { step?: Step; ticket?: string; redirect?: string | null };

const EXPIRED_MESSAGE = "Your sign-in expired. Please sign in again.";

// Google login passes the ticket in the URL fragment; password login in router state.
const readTicket = (hash: string, state: MfaState | null): MfaState => {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const step = params.get("step") || state?.step;
  return {
    step: step === "verify" || step === "setup" ? step : undefined,
    ticket: params.get("ticket") || state?.ticket || undefined,
    redirect: state?.redirect ?? null,
  };
};

const Mfa: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setCurrentWorkspace } = useUserContext();
  const [{ step, ticket, redirect }] = useState(() =>
    readTicket(location.hash, location.state as MfaState | null)
  );
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [setup, setSetup] = useState<{ qrDataUrl: string; manualKey: string } | null>(null);
  const [result, setResult] = useState<MfaLoginResult | null>(null);
  const [expired, setExpired] = useState(!ticket || !step);
  const [error, setError] = useState(!ticket || !step ? EXPIRED_MESSAGE : "");
  const [loading, setLoading] = useState(false);
  const setupStarted = useRef(false);

  const showError = (err: any) => {
    const isExpired = err.response?.status === 401;
    setExpired(isExpired);
    setError(isExpired ? EXPIRED_MESSAGE : err.response?.data?.message || "Something went wrong. Please try again.");
  };

  // Keep the ticket out of the address bar and history.
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (step !== "setup" || !ticket || setupStarted.current) return;
    setupStarted.current = true; // StrictMode runs effects twice in dev
    mfaSetupStart(ticket).then(setSetup).catch(showError);
  }, [step, ticket]);

  const finish = (data: MfaLoginResult) =>
    completeLogin(data, { setUser, setCurrentWorkspace, navigate, redirectParam: redirect ?? null });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket || !code || loading) return;
    setLoading(true);
    setError("");
    try {
      if (step === "setup") {
        setResult(await mfaSetupConfirm(ticket, code)); // show recovery codes before continuing
      } else {
        await finish(await mfaVerify(ticket, useRecovery ? { recoveryCode: code } : { code }));
      }
    } catch (err: any) {
      showError(err);
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const renderRecoveryCodes = (codes: string[]) => (
    <>
      <Typography sx={TEXT_SX}>
        Save these backup codes somewhere safe. Each one works once if you lose access to your
        authenticator app. They won't be shown again.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
          mb: 2,
          p: 2,
          borderRadius: "6px",
          backgroundColor: "#040b09",
          border: `1px solid ${THEME_COLORS.line}`,
          fontFamily: FONT_MONO,
          color: THEME_COLORS.ink,
          textAlign: "center",
        }}
      >
        {codes.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </Box>
      <Button fullWidth sx={{ ...LINK_BUTTON_SX, mb: 2 }} onClick={() => navigator.clipboard?.writeText(codes.join("\n"))}>
        Copy codes
      </Button>
      <Button fullWidth variant="contained" sx={PRIMARY_BUTTON_SX} onClick={() => result && finish(result).catch(showError)}>
        I saved them, continue
      </Button>
    </>
  );

  const renderForm = () => (
    <Box component="form" onSubmit={handleSubmit}>
      {step === "setup" ? (
        <>
          <Typography sx={TEXT_SX}>
            Scan this QR code with an authenticator app (Google Authenticator, Microsoft
            Authenticator, 1Password...), then enter the 6-digit code it shows.
          </Typography>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            {setup ? (
              <img src={setup.qrDataUrl} alt="Authenticator QR code" style={{ width: 200, height: 200, borderRadius: 6 }} />
            ) : (
              <Typography sx={{ ...TEXT_SX, color: THEME_COLORS.txtDim }}>Loading...</Typography>
            )}
          </Box>
          {setup && (
            <Typography sx={{ ...TEXT_SX, fontSize: "0.85rem", color: THEME_COLORS.txtDim, wordBreak: "break-all" }}>
              Can't scan? Enter this key:{" "}
              <Box component="span" sx={{ fontFamily: FONT_MONO, color: THEME_COLORS.ink }}>
                {setup.manualKey.replace(/(.{4})/g, "$1 ").trim()}
              </Box>
            </Typography>
          )}
        </>
      ) : (
        <Typography sx={TEXT_SX}>
          {useRecovery
            ? "Enter one of your backup codes."
            : "Enter the 6-digit code from your authenticator app."}
        </Typography>
      )}

      <TextField
        fullWidth
        autoFocus
        label={useRecovery ? "Backup code" : "6-digit code"}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        inputProps={{
          inputMode: useRecovery ? "text" : "numeric",
          autoComplete: "one-time-code",
          maxLength: useRecovery ? 11 : 6,
        }}
        sx={{ mb: 2.5 }}
        InputProps={INPUT_PROPS}
        InputLabelProps={LABEL_PROPS}
      />

      <Button
        fullWidth
        type="submit"
        variant="contained"
        disabled={loading || !code || (step === "setup" && !setup)}
        sx={{ ...PRIMARY_BUTTON_SX, mb: 2 }}
      >
        {loading ? "Verifying..." : "Verify"}
      </Button>

      {step === "verify" && (
        <Button
          fullWidth
          sx={LINK_BUTTON_SX}
          onClick={() => {
            setUseRecovery(!useRecovery);
            setCode("");
          }}
        >
          {useRecovery ? "Use authenticator code instead" : "Use a backup code instead"}
        </Button>
      )}
    </Box>
  );

  return (
    <Box sx={PAGE_SX}>
      <Paper elevation={6} sx={PANEL_SX}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <LogoContainer>
            <Logo />
          </LogoContainer>
          <Typography
            variant="h5"
            sx={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 900,
              letterSpacing: "2px",
              color: THEME_COLORS.ink,
              textShadow: `0 0 20px ${THEME_COLORS.grnGlow}`,
            }}
          >
            {step === "setup" && !result ? "Set up two-step verification" : "Two-step verification"}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={ALERT_SX}>
            {error}
          </Alert>
        )}

        {result?.recoveryCodes ? renderRecoveryCodes(result.recoveryCodes) : !expired && renderForm()}

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button sx={LINK_BUTTON_SX} onClick={() => navigate("/login")}>
            Back to sign in
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Mfa;
